<template>
  <div class="page-container">
    <div class="main shift-schedule">
      <el-tabs v-model="tabShiftType" class="shift-tabs" @tab-click="onTabChange">
        <el-tab-pane
            v-for="item in shiftTypeTabs"
            :key="item.value"
            :label="item.label"
            :name="item.value"
        />
      </el-tabs>
      <page
          :url="url"
          :form="form"
          ref="pageRef"
          @reset="reset"
      >
        <template slot="tableSearch">
          <el-form-item :label="$t('text.csShiftStaff')">
            <el-select
                v-model="form.messagingAppUserId"
                filterable
                clearable
                :placeholder="$t('message.chooseOne')"
                style="width: 180px"
            >
              <el-option
                  v-for="user in appUserInfo"
                  :key="user.id"
                  :label="formatUserOption(user)"
                  :value="user.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.enabled')">
            <el-select
                v-model="form.enabled"
                clearable
                :placeholder="$t('message.chooseOne')"
                style="width: 120px"
            >
              <el-option :label="$t('creditFlag.01')" :value="true"/>
              <el-option :label="$t('creditFlag.00')" :value="false"/>
            </el-select>
          </el-form-item>
        </template>

        <template slot="buttonArea">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openAdd">
            {{ $t('text.shiftAdd') }}
          </el-button>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openBatchAdd">
            {{ $t('text.csShiftBatchAdd') }}
          </el-button>
          <el-button type="primary" size="small" icon="el-icon-user" plain @click="openUserManage">
            {{ $t('text.existingUsers') }}
          </el-button>
          <el-button type="info" size="small" :loading="onDutyLoading" @click="loadOnDuty">
            {{ $t('text.csShiftOnDutyPreview') }}
          </el-button>
        </template>

        <template slot="pageHeader">
          <el-alert
              :title="$t('text.csShiftFixedTimeHint')"
              type="info"
              :closable="false"
              show-icon
              style="margin: 8px 0 12px"
          />
          <el-alert
              v-if="onDutyText"
              :title="onDutyText"
              type="success"
              show-icon
              :closable="true"
              style="margin-bottom: 12px"
              @close="onDutyText = ''"
          />
        </template>

        <template slot="tableColumn">
          <el-table-column :label="$t('text.csShiftName')" width="140" align="center" header-align="center">
            <template slot-scope="scope">
              {{ scope.row.shiftName || '-' }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('text.csShiftTimeRange')" width="240" align="center" header-align="center">
            <template slot-scope="scope">
              {{ formatShiftRange(scope.row) }}
            </template>
          </el-table-column>
          <el-table-column prop="daysOfWeek" :label="$t('text.csShiftDayOfWeek')" min-width="220" align="center" header-align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ formatDaysOfWeek(scope.row.daysOfWeek) }}
            </template>
          </el-table-column>
          <el-table-column prop="userName" :label="$t('text.csShiftStaff')" width="160" align="center" header-align="center"/>
          <el-table-column prop="enabled" :label="$t('text.enabled')" width="100" align="center" header-align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.enabled ? 'success' : 'info'" size="small">
                {{ scope.row.enabled ? $t('creditFlag.01') : $t('creditFlag.00') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column :label="$t('text.actions')" width="180" align="center" header-align="center">
            <template slot-scope="scope">
              <el-button type="primary" size="mini" plain @click="openEdit(scope.row)">
                {{ $t('text.edit') }}
              </el-button>
              <el-button type="danger" size="mini" plain @click="handleDelete(scope.row)">
                {{ $t('text.delete') }}
              </el-button>
            </template>
          </el-table-column>
        </template>
      </page>

      <el-dialog
          :visible.sync="editVisible"
          :title="editForm.id ? $t('text.edit') : $t('text.shiftAdd')"
          width="720px"
          @close="closeEdit"
      >
        <el-form ref="editFormRef" :model="editForm" :rules="rules" label-width="130px">
          <el-form-item :label="$t('text.csShiftName')">
            <el-input v-model="editForm.shiftName" clearable :placeholder="$t('text.csShiftNameOptional')"/>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftTimeRange')" required>
            <div class="time-preset">
              <el-tag
                  v-for="preset in timePresets"
                  :key="preset.key"
                  :type="isTimePresetActive(editForm, preset.key) ? 'primary' : 'info'"
                  effect="plain"
                  class="time-preset-tag"
                  @click="applyTimePreset(preset.key, 'editForm')"
              >
                {{ preset.label }}
              </el-tag>
            </div>
            <div class="time-pickers">
              <el-time-picker
                  v-model="editForm.startTime"
                  format="HH:mm"
                  value-format="HH:mm:ss"
                  :placeholder="$t('text.csShiftStartTime')"
                  style="width: 160px"
              />
              <span class="time-sep">-</span>
              <el-time-picker
                  v-model="editForm.endTime"
                  format="HH:mm"
                  value-format="HH:mm:ss"
                  :placeholder="$t('text.csShiftEndTime')"
                  style="width: 160px"
              />
              <span class="time-hint">{{ $t('text.csShiftBeijingTime') }}</span>
            </div>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftDayOfWeek')">
            <div class="weekday-quick">
              <el-button size="mini" @click="applyWeekdayPreset('weekdays', 'editForm')">{{ $t('text.csShiftWeekdays') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('weekend', 'editForm')">{{ $t('text.csShiftWeekend') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('all', 'editForm')">{{ $t('text.csShiftEveryDay') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('clear', 'editForm')">{{ $t('text.clear') }}</el-button>
            </div>
            <el-select
                v-model="editForm.daysOfWeekList"
                multiple
                clearable
                collapse-tags
                :placeholder="$t('text.csShiftEveryDayPlaceholder')"
                style="width: 100%; margin-top: 8px"
            >
              <el-option v-for="item in dayOptions" :key="item.value" :label="item.label" :value="item.value"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftStaff')" prop="messagingAppUserId">
            <el-select
                v-model="editForm.messagingAppUserId"
                filterable
                clearable
                :placeholder="$t('message.chooseOne')"
                style="width: 100%"
            >
              <el-option
                  v-for="user in appUserInfo"
                  :key="user.id"
                  :label="formatUserOption(user)"
                  :value="user.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.enabled')">
            <el-switch v-model="editForm.enabled" :active-text="$t('creditFlag.01')" :inactive-text="$t('creditFlag.00')"/>
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button @click="closeEdit">{{ $t('text.cancel') }}</el-button>
          <el-button type="primary" :loading="saving" @click="submitEdit">{{ $t('text.confirm') }}</el-button>
        </div>
      </el-dialog>

      <el-dialog
          :visible.sync="batchVisible"
          :title="$t('text.csShiftBatchAdd')"
          width="760px"
          @close="closeBatchAdd"
      >
        <el-form ref="batchFormRef" :model="batchForm" :rules="batchRules" label-width="130px">
          <el-form-item :label="$t('text.csShiftName')">
            <el-input v-model="batchForm.shiftName" clearable :placeholder="$t('text.csShiftNameOptional')"/>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftTimeRange')" required>
            <div class="time-preset">
              <el-tag
                  v-for="preset in timePresets"
                  :key="preset.key"
                  :type="isTimePresetActive(batchForm, preset.key) ? 'primary' : 'info'"
                  effect="plain"
                  class="time-preset-tag"
                  @click="applyTimePreset(preset.key, 'batchForm')"
              >
                {{ preset.label }}
              </el-tag>
            </div>
            <div class="time-pickers">
              <el-time-picker
                  v-model="batchForm.startTime"
                  format="HH:mm"
                  value-format="HH:mm:ss"
                  :placeholder="$t('text.csShiftStartTime')"
                  style="width: 160px"
              />
              <span class="time-sep">-</span>
              <el-time-picker
                  v-model="batchForm.endTime"
                  format="HH:mm"
                  value-format="HH:mm:ss"
                  :placeholder="$t('text.csShiftEndTime')"
                  style="width: 160px"
              />
              <span class="time-hint">{{ $t('text.csShiftBeijingTime') }}</span>
            </div>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftDayOfWeek')">
            <div class="weekday-quick">
              <el-button size="mini" @click="applyWeekdayPreset('weekdays', 'batchForm')">{{ $t('text.csShiftWeekdays') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('weekend', 'batchForm')">{{ $t('text.csShiftWeekend') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('all', 'batchForm')">{{ $t('text.csShiftEveryDay') }}</el-button>
              <el-button size="mini" @click="applyWeekdayPreset('clear', 'batchForm')">{{ $t('text.clear') }}</el-button>
            </div>
            <el-select
                v-model="batchForm.daysOfWeekList"
                multiple
                clearable
                collapse-tags
                :placeholder="$t('text.csShiftEveryDayPlaceholder')"
                style="width: 100%; margin-top: 8px"
            >
              <el-option v-for="item in dayOptions" :key="item.value" :label="item.label" :value="item.value"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.csShiftStaff')" prop="messagingAppUserIds">
            <el-select
                v-model="batchForm.messagingAppUserIds"
                multiple
                filterable
                collapse-tags
                :placeholder="$t('text.csShiftBatchStaffPlaceholder')"
                style="width: 100%"
            >
              <el-option
                  v-for="user in appUserInfo"
                  :key="user.id"
                  :label="formatUserOption(user)"
                  :value="user.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.enabled')">
            <el-switch v-model="batchForm.enabled" :active-text="$t('creditFlag.01')" :inactive-text="$t('creditFlag.00')"/>
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button @click="closeBatchAdd">{{ $t('text.cancel') }}</el-button>
          <el-button type="primary" :loading="batchSaving" @click="submitBatchAdd">{{ $t('text.confirm') }}</el-button>
        </div>
      </el-dialog>

      <MessagingAppAtUserSelect
          v-if="showUserManage"
          :show.sync="showUserManage"
          :appUserInfo="appUserInfoFull"
          :roleList="roleList"
          @update-messaging-user-info="onMessagingUserUpdated"
      />
    </div>
  </div>
</template>

<script>
import Page from "@/components/Page.vue";
import MessagingAppAtUserSelect from "@/views/developer/components/messagingAppAt/MessagingAppAtUserSelect.vue";

const API = '/shiftSchedule';

const SHIFT_TYPE_TABS = [
  {value: 'CS', labelKey: 'text.shiftCsTab'},
  {value: 'PAYOUT_REVIEW', labelKey: 'text.shiftOperatorTab'}
];

const TIME_PRESETS = {
  MORNING: {
    shiftName: '早班',
    startTime: '10:00:00',
    endTime: '22:00:00'
  },
  NIGHT: {
    shiftName: '夜班',
    startTime: '22:00:00',
    endTime: '10:00:00'
  }
};

const WEEKDAY_PRESETS = {
  weekdays: [1, 2, 3, 4, 5],
  weekend: [6, 7],
  all: [],
  clear: []
};

const EMPTY_FORM = () => ({
  id: null,
  shiftName: '',
  startTime: '',
  endTime: '',
  daysOfWeek: '',
  daysOfWeekList: [],
  messagingAppUserId: null,
  enabled: true
});

const EMPTY_BATCH_FORM = () => ({
  shiftName: '',
  startTime: '',
  endTime: '',
  daysOfWeekList: [],
  messagingAppUserIds: [],
  enabled: true
});

const EMPTY_QUERY = (shiftType) => ({
  shiftType: shiftType || 'CS',
  messagingAppUserId: '',
  enabled: ''
});

export default {
  name: 'ShiftSchedule',
  components: {
    page: Page,
    MessagingAppAtUserSelect
  },
  data() {
    return {
      tabShiftType: 'CS',
      url: `${API}/selectList`,
      form: EMPTY_QUERY('CS'),
      appUserInfo: [],
      appUserInfoFull: [],
      roleList: [],
      showUserManage: false,
      editVisible: false,
      batchVisible: false,
      editForm: EMPTY_FORM(),
      batchForm: EMPTY_BATCH_FORM(),
      saving: false,
      batchSaving: false,
      onDutyLoading: false,
      onDutyText: '',
      dayOptions: [],
      rules: {
        messagingAppUserId: [{required: true, message: this.$t('message.chooseOne'), trigger: 'change'}]
      },
      batchRules: {
        messagingAppUserIds: [{type: 'array', required: true, min: 1, message: this.$t('text.csShiftBatchStaffPlaceholder'), trigger: 'change'}]
      }
    };
  },
  computed: {
    shiftTypeTabs() {
      return SHIFT_TYPE_TABS.map(item => ({
        value: item.value,
        label: this.$t(item.labelKey)
      }));
    },
    timePresets() {
      return [
        {key: 'MORNING', label: this.$t('text.csShiftMorningTag')},
        {key: 'NIGHT', label: this.$t('text.csShiftNightTag')}
      ];
    }
  },
  methods: {
    reset() {
      this.form.messagingAppUserId = '';
      this.form.enabled = '';
      this.form.shiftType = this.tabShiftType;
    },
    onTabChange() {
      this.form.shiftType = this.tabShiftType;
      this.onDutyText = '';
      this.$nextTick(() => {
        if (this.$refs.pageRef) {
          this.$refs.pageRef.onSubmit();
        }
      });
    },
    formatDaysOfWeek(daysOfWeek) {
      if (!daysOfWeek) {
        return this.$t('text.csShiftEveryDay');
      }
      return String(daysOfWeek).split(',')
          .map(day => this.formatDayOfWeek(Number(day.trim())))
          .join('、');
    },
    formatDayOfWeek(day) {
      if (!day) {
        return this.$t('text.csShiftEveryDay');
      }
      const found = this.dayOptions.find(item => item.value === day);
      return found ? found.label : day;
    },
    parseDaysOfWeekList(daysOfWeek) {
      if (!daysOfWeek) {
        return [];
      }
      return String(daysOfWeek).split(',')
          .map(day => Number(day.trim()))
          .filter(day => !Number.isNaN(day));
    },
    serializeDaysOfWeek(daysOfWeekList) {
      if (!daysOfWeekList || daysOfWeekList.length === 0) {
        return '';
      }
      return [...daysOfWeekList].sort((a, b) => a - b).join(',');
    },
    applyWeekdayPreset(presetKey, formName) {
      const target = this[formName];
      target.daysOfWeekList = [...(WEEKDAY_PRESETS[presetKey] || [])];
    },
    formatShiftRange(row) {
      if (!row || !row.startTime || !row.endTime) {
        return '-';
      }
      return `${this.formatClock(row.startTime)} - ${this.formatClock(row.endTime)} (${this.$t('text.csShiftBeijingTime')})`;
    },
    formatClock(raw) {
      return raw && raw.length >= 5 ? raw.substring(0, 5) : raw;
    },
    isTimePresetActive(form, presetKey) {
      const preset = TIME_PRESETS[presetKey];
      return form && preset && form.startTime === preset.startTime && form.endTime === preset.endTime;
    },
    applyTimePreset(presetKey, formName) {
      const preset = TIME_PRESETS[presetKey];
      if (!preset) {
        return;
      }
      const target = this[formName];
      target.shiftName = preset.shiftName;
      target.startTime = preset.startTime;
      target.endTime = preset.endTime;
    },
    formatUserOption(user) {
      return user.name || '-';
    },
    loadUsers() {
      return this.$axios.get(`${API}/staffOptions`, {params: {}}).then(res => {
        this.appUserInfo = res.data || [];
      });
    },
    loadMessagingUsers() {
      return this.$axios.get('/messagingAppUserInfo/selectOption', {params: {}}).then(res => {
        const list = res.data || [];
        this.appUserInfoFull = list;
        this.roleList = [...new Set(list.map(item => item.role).filter(Boolean))];
      });
    },
    openUserManage() {
      this.loadMessagingUsers().finally(() => {
        this.showUserManage = true;
      });
    },
    onMessagingUserUpdated() {
      this.loadUsers();
      this.loadMessagingUsers();
    },
    loadOnDuty() {
      this.onDutyLoading = true;
      this.$axios.get(`${API}/onDuty`, {params: {shiftType: this.tabShiftType}}).then(res => {
        const staffList = Array.isArray(res.data) ? res.data : [];
        const names = staffList.map(item => item.name).filter(Boolean);
        this.onDutyText = names.length > 0
            ? `${this.$t('text.csShiftOnDutyNow')} (${this.$t('text.csShiftBeijingTime')}): ${names.join(', ')}`
            : this.$t('text.csShiftOnDutyEmpty');
      }).finally(() => {
        this.onDutyLoading = false;
      });
    },
    openAdd() {
      this.editForm = EMPTY_FORM();
      this.editVisible = true;
      this.$nextTick(() => {
        if (this.$refs.editFormRef) {
          this.$refs.editFormRef.clearValidate();
        }
      });
    },
    openEdit(row) {
      this.editForm = {
        ...EMPTY_FORM(),
        ...row,
        daysOfWeekList: this.parseDaysOfWeekList(row.daysOfWeek)
      };
      if (this.editForm.enabled === undefined || this.editForm.enabled === null) {
        this.editForm.enabled = true;
      }
      this.editVisible = true;
      this.$nextTick(() => {
        if (this.$refs.editFormRef) {
          this.$refs.editFormRef.clearValidate();
        }
      });
    },
    closeEdit() {
      this.editVisible = false;
      this.editForm = EMPTY_FORM();
    },
    openBatchAdd() {
      this.batchForm = EMPTY_BATCH_FORM();
      this.batchVisible = true;
      this.$nextTick(() => {
        if (this.$refs.batchFormRef) {
          this.$refs.batchFormRef.clearValidate();
        }
      });
    },
    closeBatchAdd() {
      this.batchVisible = false;
      this.batchForm = EMPTY_BATCH_FORM();
    },
    validateTimes(form) {
      if (!form.startTime || !form.endTime) {
        this.$notify.error({title: this.$t('text.error'), message: this.$t('text.csShiftTimeRequired')});
        return false;
      }
      return true;
    },
    submitEdit() {
      this.$refs.editFormRef.validate(valid => {
        if (!valid || !this.validateTimes(this.editForm)) {
          return;
        }
        this.saving = true;
        const payload = {
          id: this.editForm.id,
          shiftType: this.tabShiftType,
          shiftName: this.editForm.shiftName,
          startTime: this.editForm.startTime,
          endTime: this.editForm.endTime,
          daysOfWeek: this.serializeDaysOfWeek(this.editForm.daysOfWeekList),
          messagingAppUserId: this.editForm.messagingAppUserId,
          enabled: this.editForm.enabled,
          displayOrder: this.editForm.displayOrder || 0
        };
        const request = payload.id
            ? this.$axios.put(`${API}/edit`, payload)
            : this.$axios.post(`${API}/add`, payload);
        request.then(res => {
          if (res.code === 200) {
            this.$notify.success({title: this.$t('text.success'), message: res.msg});
            this.closeEdit();
            this.$refs.pageRef.onSubmit();
          } else {
            this.$notify.error({title: this.$t('text.error'), message: res.msg});
          }
        }).finally(() => {
          this.saving = false;
        });
      });
    },
    submitBatchAdd() {
      this.$refs.batchFormRef.validate(valid => {
        if (!valid || !this.validateTimes(this.batchForm)) {
          return;
        }
        this.batchSaving = true;
        const payload = {
          shiftType: this.tabShiftType,
          shiftName: this.batchForm.shiftName,
          startTime: this.batchForm.startTime,
          endTime: this.batchForm.endTime,
          daysOfWeek: this.serializeDaysOfWeek(this.batchForm.daysOfWeekList),
          messagingAppUserIds: this.batchForm.messagingAppUserIds,
          enabled: this.batchForm.enabled
        };
        this.$axios.post(`${API}/batchAdd`, payload, {
          headers: {'Content-Type': 'application/json'},
          transformRequest: [(data) => JSON.stringify(data)]
        }).then(res => {
          if (res.code === 200) {
            this.$notify.success({title: this.$t('text.success'), message: res.msg});
            this.closeBatchAdd();
            this.$refs.pageRef.onSubmit();
          } else {
            this.$notify.error({title: this.$t('text.error'), message: res.msg});
          }
        }).finally(() => {
          this.batchSaving = false;
        });
      });
    },
    handleDelete(row) {
      this.$confirm(this.$t('text.confirmDeleteShift'), this.$t('text.delete'), {
        type: 'warning'
      }).then(() => {
        this.$axios.delete(`${API}/delete`, {params: {id: row.id}}).then(res => {
          if (res.code === 200) {
            this.$notify.success({title: this.$t('text.success'), message: res.msg});
            this.$refs.pageRef.onSubmit();
          } else {
            this.$notify.error({title: this.$t('text.error'), message: res.msg});
          }
        });
      }).catch(() => {});
    }
  },
  created() {
    this.dayOptions = [
      {value: 1, label: this.$t('text.weekday.mon')},
      {value: 2, label: this.$t('text.weekday.tue')},
      {value: 3, label: this.$t('text.weekday.wed')},
      {value: 4, label: this.$t('text.weekday.thu')},
      {value: 5, label: this.$t('text.weekday.fri')},
      {value: 6, label: this.$t('text.weekday.sat')},
      {value: 7, label: this.$t('text.weekday.sun')}
    ];
    this.loadUsers();
    this.$nextTick(() => {
      if (this.$refs.pageRef) {
        this.$refs.pageRef.onSubmit();
      }
    });
  }
};
</script>

<style lang="scss" scoped>
.shift-schedule {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.shift-tabs {
  flex-shrink: 0;
  padding: 16px 16px 0;
  ::v-deep .el-tabs__header {
    margin: 0;
  }
  ::v-deep .el-tabs__content {
    display: none;
  }
}
.shift-schedule ::v-deep .page {
  flex: 1;
  min-height: 0;
  max-height: none !important;
  display: flex;
  flex-direction: column;
}
.weekday-quick,
.time-preset,
.time-pickers {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.time-preset {
  margin-bottom: 8px;
}
.time-preset-tag {
  cursor: pointer;
}
.time-sep {
  color: #909399;
}
.time-hint {
  color: #909399;
  font-size: 12px;
}
</style>
